import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiCalls } from '../../../../../api/api';
import { CreateActivityDto, UpdateActivityDto } from '../../../../../api/generated/data-contracts';

export const ACTIVITIES_QUERY_KEY = ['activities'];

interface UseActivityMutationsProps {
    onSetItemLoadingState: (identifier: string, field: 'isSaving' | 'isDeleting', value: boolean) => void;
    onShowSuccessFeedback: (identifier: string) => void;
    onSetErrorMessage: (message: string | null) => void;
    onRemoveNewItems: () => void;
}

export const useActivityMutations = ({ onSetItemLoadingState, onShowSuccessFeedback, onSetErrorMessage, onRemoveNewItems }: UseActivityMutationsProps) => {
    const queryClient = useQueryClient();

    const createActivityMutation = useMutation({
        mutationFn: ({ activity, tempId }: { activity: CreateActivityDto; tempId: string }) => {
            onSetItemLoadingState(tempId, 'isSaving', true);
            return ApiCalls.configurationActivitiesCreate(activity);
        },
        onSuccess: (_, { tempId }) => {
            onSetErrorMessage(null);
            onSetItemLoadingState(tempId, 'isSaving', false);
            onRemoveNewItems();
            queryClient.invalidateQueries({ queryKey: ACTIVITIES_QUERY_KEY });
        },
        onError: (error: any, { tempId }) => {
            console.error('Error creating activity:', error);
            onSetItemLoadingState(tempId, 'isSaving', false);
            onSetErrorMessage('An error occurred while creating the activity. Please try again.');
        },
    });

    const updateActivityMutation = useMutation({
        mutationFn: ({ id, activity }: { id: string; activity: UpdateActivityDto }) => {
            onSetItemLoadingState(id, 'isSaving', true);
            return ApiCalls.configurationActivitiesUpdate({ id }, activity);
        },
        onSuccess: (_, { id }) => {
            onSetErrorMessage(null);
            onSetItemLoadingState(id, 'isSaving', false);
            onShowSuccessFeedback(id);
            queryClient.invalidateQueries({ queryKey: ACTIVITIES_QUERY_KEY });
        },
        onError: (error: any, { id }) => {
            console.error('Error updating activity:', error);
            onSetItemLoadingState(id, 'isSaving', false);
            onSetErrorMessage('An error occurred while updating the activity. Please try again.');
        },
    });

    const deleteActivityMutation = useMutation({
        mutationFn: (id: string) => {
            onSetItemLoadingState(id, 'isDeleting', true);
            return ApiCalls.configurationActivitiesDelete({ id });
        },
        onSuccess: (_, id) => {
            onSetErrorMessage(null);
            onSetItemLoadingState(id, 'isDeleting', false);
            queryClient.invalidateQueries({ queryKey: ACTIVITIES_QUERY_KEY });
        },
        onError: (error: any, id) => {
            console.error('Error deleting activity:', error);
            onSetItemLoadingState(id, 'isDeleting', false);
            if (error?.status === 409) {
                onSetErrorMessage('Cannot delete this activity because it is currently in use.');
            } else {
                onSetErrorMessage('An error occurred while deleting the activity. Please try again.');
            }
        },
    });

    return {
        createActivityMutation,
        updateActivityMutation,
        deleteActivityMutation,
    };
};
